/**
 * Teletherapy guardian signing uses the existing SignWell intent and send path.
 * It is not an approved manual-note exception.
 */

export type SoapEsignLane = 'manual' | 'teletherapy' | 'approved-assignment';

export type SoapGuardianEsignAccess =
  | { allowed: true; lane: SoapEsignLane }
  | { allowed: false; code: 'MANUAL_SOAP_NOT_PERMITTED'; error: string };

const INELIGIBLE_SOAP_ERROR =
  'Manual SOAP is not enabled for this assignment. Use Mobile SOAP or contact Office.';

function text(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function metadataOf(doc: { metadata_json?: unknown } | null | undefined): Record<string, unknown> {
  const raw = doc?.metadata_json;
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  try {
    const parsed = JSON.parse(String(raw));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch {
    return {};
  }
  return {};
}

export function isTeletherapyPlaceOfService(value: unknown): boolean {
  const pos = text(value);
  return pos === '02' || pos === '10';
}

export function sessionRequiresInPersonGuardianPad(setting: unknown): boolean {
  return text(setting).toUpperCase() !== 'TELETHERAPY';
}

/** Office remote-signature approval stored on the current client-provider assignment. */
export function assignmentRemoteSoapApproved(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/**
 * Teletherapy is always the remote lane.
 * Clinic and home stay on the pad unless the therapist chooses SignWell on an approved assignment.
 * An unapproved clinic or home visit cannot opt out of the pad.
 */
export function mobileSoapRequiresInPersonPad(input: {
  setting: unknown;
  assignmentApproved?: unknown;
  signatureChoice?: unknown;
}): boolean {
  if (!sessionRequiresInPersonGuardianPad(input.setting)) return false;
  if (!assignmentRemoteSoapApproved(input.assignmentApproved)) return true;
  return text(input.signatureChoice).toUpperCase() !== 'REMOTE';
}

export function isManualSoapDocument(doc: {
  doc_type?: unknown;
  is_manual_soap?: unknown;
  source?: unknown;
  metadata_json?: unknown;
} | null | undefined): boolean {
  if (text(doc?.doc_type).toUpperCase() !== 'SOAP') return false;
  if (doc?.is_manual_soap === true || doc?.is_manual_soap === 1 || doc?.is_manual_soap === 'true') return true;
  if (text(doc?.source).toUpperCase() === 'MANUAL_DESKTOP') return true;
  const meta = metadataOf(doc);
  return meta.is_manual_soap === true;
}

export function isPointOfCareSoap(doc: {
  doc_type?: unknown;
  source?: unknown;
  metadata_json?: unknown;
} | null | undefined): boolean {
  if (text(doc?.doc_type).toUpperCase() !== 'SOAP') return false;
  if (text(doc?.source).toUpperCase() === 'POINT_OF_CARE') return true;
  const meta = metadataOf(doc);
  return text(meta.source).toUpperCase() === 'POINT_OF_CARE' || meta.is_point_of_care === true;
}

export function isEligibleTeletherapySoap(doc: {
  doc_type?: unknown;
  is_manual_soap?: unknown;
  source?: unknown;
  metadata_json?: unknown;
  place_of_service?: unknown;
} | null | undefined): boolean {
  if (!isPointOfCareSoap(doc)) return false;
  if (isManualSoapDocument(doc)) return false;
  return isTeletherapyPlaceOfService(doc?.place_of_service);
}

export function soapGuardianEsignAccess(doc: {
  doc_type?: unknown;
  is_manual_soap?: unknown;
  source?: unknown;
  metadata_json?: unknown;
  place_of_service?: unknown;
  manual_soap_approved?: unknown;
  assignment_manual_soap_approved?: unknown;
} | null | undefined): SoapGuardianEsignAccess {
  if (text(doc?.doc_type).toUpperCase() !== 'SOAP') {
    return { allowed: false, code: 'MANUAL_SOAP_NOT_PERMITTED', error: INELIGIBLE_SOAP_ERROR };
  }
  if (isManualSoapDocument(doc)) return { allowed: true, lane: 'manual' };
  if (isEligibleTeletherapySoap(doc)) return { allowed: true, lane: 'teletherapy' };
  const assignmentApproved = doc?.assignment_manual_soap_approved ?? doc?.manual_soap_approved;
  if (assignmentRemoteSoapApproved(assignmentApproved)) {
    return { allowed: true, lane: 'approved-assignment' };
  }
  return { allowed: false, code: 'MANUAL_SOAP_NOT_PERMITTED', error: INELIGIBLE_SOAP_ERROR };
}

const ACTIVE_ENVELOPE_STATUSES = new Set(['SENT', 'PENDING', 'VIEWED', 'COMPLETED']);

export function explicitSendFailureRecord(input: {
  existingStatus?: unknown;
  existingEnvelopeId?: unknown;
  failureReason?: unknown;
}): { persist: false } | { persist: true; status: 'FAILED'; envelopeId: null; failureReason: string } {
  const status = text(input.existingStatus).toUpperCase();
  const envelopeId = text(input.existingEnvelopeId);
  if (envelopeId && ACTIVE_ENVELOPE_STATUSES.has(status)) {
    return { persist: false };
  }
  const reason = text(input.failureReason) || 'SignWell request did not send. No envelope was created.';
  return { persist: true, status: 'FAILED', envelopeId: null, failureReason: reason };
}
